import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { faUserPlus, faUsers } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { GlobalSearchModal } from "@/components/GlobalSearchModal/GlobalSearchModal";
import { ReferralSearchContext } from "@/components/GlobalSearchModal/referralContext";
import { DEPARTMENT_OPTIONS } from "@/pages/Private/pages/CompanyEnrich/constants/department.constants";
import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";
import { ListPerson } from "@/pages/Private/pages/List/schema/list";
import { ProspectInterest } from "@/enum/prospect.enum";

/**
 * Contextual wrong-person referral CTA (PREC-3584), rendered next to the existing
 * sentiment chip ("Marked as Wrong person by Signalist") under a received reply. Opens
 * the find-a-person modal prefilled from the reply's extracted referral (named person
 * or department) plus the lead's company domain and country; without referral data it
 * falls back to a generic "Find another contact" with the company prefill only.
 */
export const WrongPersonReferralCta: FunctionComponent<{
	conversation: Conversation;
	lead?: ListPerson;
}> = ({ conversation, lead }) => {
	const { t } = useTranslation();
	const [isSearchOpened, setIsSearchOpened] = useState(false);

	const isReceived =
		conversation.type === "mail_received" || conversation.type === "message_received";

	if (!isReceived || conversation.sentiment !== ProspectInterest.WRONG_PERSON) {
		return null;
	}

	const referral = conversation.referralData;
	const organization = lead?.prospect?.organization;
	const departmentTitle = referral?.department
		? (DEPARTMENT_OPTIONS.find((option) => option.id === referral.department)?.title ??
			referral.department)
		: undefined;

	const isPersonReferral = referral?.type === "person" && !!referral.name;
	const ctaLabel = isPersonReferral
		? t("inbox.thread.wrongPerson.reachOutTo", { name: referral?.name })
		: departmentTitle
			? t("inbox.thread.wrongPerson.findSomeoneIn", { department: departmentTitle })
			: t("inbox.thread.wrongPerson.findAnotherContact");

	const footerNote = isPersonReferral
		? t("inbox.thread.wrongPerson.footerNotePerson", { name: referral?.name })
		: departmentTitle
			? t("inbox.thread.wrongPerson.footerNoteDepartment", { department: departmentTitle })
			: t("inbox.thread.wrongPerson.footerNoteGeneric");

	const referralContext: ReferralSearchContext = {
		company: organization?.primaryDomain
			? {
					name: organization.name ?? organization.primaryDomain,
					domain: organization.primaryDomain,
				}
			: undefined,
		department: referral?.department ?? undefined,
		countryName: organization?.country ?? undefined,
		nameQuery: isPersonReferral ? (referral?.name ?? undefined) : undefined,
		footerNote,
	};

	return (
		<>
			<button
				className="flex items-center gap-1.5 mt-2 h-fit self-center bg-blue-action text-white rounded-full px-3 py-1 text-xs font-semibold cursor-pointer hover:bg-blue-action-dark shadow-sm"
				data-testid="wrong-person-cta"
				onClick={() => setIsSearchOpened(true)}
			>
				<Icon icon={isPersonReferral ? faUserPlus : faUsers} />
				{ctaLabel}
			</button>
			{isSearchOpened && (
				<GlobalSearchModal
					isOpened={isSearchOpened}
					setIsOpened={setIsSearchOpened}
					referralContext={referralContext}
				/>
			)}
		</>
	);
};
